const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

function rms(analyser, buf) {
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i += 1) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
}

// Loudness of a decoded clip every 20 ms, normalised to the clip's own loud parts so quiet voices still move the mouth.
function envelope(buffer) {
  const data = buffer.getChannelData(0);
  const step = Math.max(1, Math.floor(buffer.sampleRate * 0.02));
  const values = new Float32Array(Math.ceil(data.length / step));
  for (let i = 0; i < values.length; i += 1) {
    let sum = 0;
    const end = Math.min(data.length, (i + 1) * step);
    for (let j = i * step; j < end; j += 1) sum += data[j] * data[j];
    values[i] = Math.sqrt(sum / Math.max(1, end - i * step));
  }
  const sorted = Float32Array.from(values).sort();
  const loud = sorted[Math.floor(sorted.length * 0.95)] || 1;
  for (let i = 0; i < values.length; i += 1) values[i] = Math.min(1, values[i] / loud);
  return values;
}

// One audio element + level meters shared by the session. Speech plays straight through the audio element (most
// reliable on mobile); its loudness envelope is decoded separately and read at the element's current playback time,
// so the avatar's mouth follows what is actually being heard and stops the instant playback stops.
export class AudioRig {
  el = null;
  ctx = null;
  env = null;
  mic = null;
  micSource = null;
  buf = new Uint8Array(512);
  smooth = 0;
  finish = null;

  // Must run inside a tap so mobile browsers allow the spoken reply to play later.
  unlock() {
    if (!this.el) {
      this.el = new Audio();
      this.el.playsInline = true;
      this.el.setAttribute("playsinline", "");
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx && !this.ctx) {
      try { this.ctx = new Ctx(); } catch { this.ctx = null; }
    }
    this.ctx?.resume?.().catch(() => {});
    if (this.el.paused) {
      this.el.src = SILENT;
      this.el.play().catch(() => {});
    }
  }

  async decode(blob, url) {
    if (!this.ctx) return;
    try {
      const bytes = await blob.arrayBuffer();
      const buffer = await new Promise((resolve, reject) => {
        const p = this.ctx.decodeAudioData(bytes, resolve, reject);
        if (p?.then) p.then(resolve, reject);
      });
      if (this.el?.src === url) this.env = envelope(buffer);
    } catch {
      /* keep the fallback movement */
    }
  }

  playing() {
    return Boolean(this.el && !this.el.paused && !this.el.ended && this.el.src && !this.el.src.startsWith("data:"));
  }

  play(blob) {
    if (this.ctx?.state === "suspended") this.ctx.resume().catch(() => {});
    const url = URL.createObjectURL(blob);
    this.env = null;
    this.el.src = url;
    this.decode(blob, url);
    return new Promise((resolve, reject) => {
      const end = () => { URL.revokeObjectURL(url); this.finish = null; resolve(); };
      this.finish = end;
      this.el.onended = end;
      this.el.onerror = end;
      this.el.play().catch((e) => { URL.revokeObjectURL(url); this.finish = null; reject(e); });
    });
  }

  stop() {
    this.el?.pause();
    this.finish?.();
  }

  attachMic(stream) {
    if (!this.ctx) return;
    try {
      this.micSource = this.ctx.createMediaStreamSource(stream);
      this.mic = this.ctx.createAnalyser();
      this.mic.fftSize = 512;
      this.micSource.connect(this.mic);
    } catch {
      this.mic = null;
    }
  }

  detachMic() {
    try { this.micSource?.disconnect(); } catch { /* already disconnected */ }
    this.micSource = null;
    this.mic = null;
  }

  speechLevel() {
    if (!this.playing()) return 0;
    if (!this.env) return 0.3 + 0.25 * Math.sin(performance.now() / 90) * Math.sin(performance.now() / 230);
    // Read slightly ahead: lips lead the sound a little in natural speech.
    return this.env[Math.floor((this.el.currentTime + 0.04) / 0.02)] || 0;
  }

  level(kind) {
    const raw = kind === "mic" ? (this.mic ? rms(this.mic, this.buf) : 0) : kind === "speech" ? this.speechLevel() : 0;
    this.smooth = this.smooth * 0.5 + raw * 0.5;
    return this.smooth;
  }

  close() {
    this.stop();
    this.detachMic();
    this.ctx?.close?.().catch(() => {});
    this.ctx = null;
    this.env = null;
  }
}
